import React, { useEffect,useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Badge,
} from "reactstrap";
import {
  ClipboardCheck, AlertTriangle, CheckCircle, MapPin,
  BarChart2, FileText, LayoutList,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getAllFilledForms, TYPE_NAMES, SCOPE_NAMES, TARGET_NAMES,
} from "../../services/sahaInspectionService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

// ── Yardımcılar ───────────────────────────────────────────────────────────────
const LEVEL_CFG = {
  1: { get label() { return i18n.t("saha:sahaAnaSayfa.denetim"); },     color: "primary", icon: <ClipboardCheck size={16} /> },
  2: { get label() { return i18n.t("saha:sahaAnaSayfa.uygunsuzluk"); }, color: "warning", icon: <AlertTriangle  size={16} /> },
  3: { get label() { return i18n.t("saha:sahaAnaSayfa.kapaliDof"); },  color: "success", icon: <CheckCircle    size={16} /> },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
function SahaAnaSayfa() {
  const { t } = useTranslation("saha");
  const navigate       = useNavigate();
  const [forms, setForms] = useState([]);

  useEffect(() => {
    document.title = i18n.t("saha:sahaAnaSayfa.fdsSahaAnaSayfa");
    getAllFilledForms().then(setForms);
  }, []);

  // Özet istatistikler
  const totalDenetim      = forms.filter((f) => f.inspectionLevelId === 1).length;
  const openUygunsuzluk   = forms.filter((f) => f.inspectionLevelId === 2 && !f.isDone).length;
  const closedDof         = forms.filter((f) => f.inspectionLevelId === 3 &&  f.isDone).length;
  const inProgress        = forms.filter((f) => f.inspectionLevelId === 1 && !f.isDone).length;

  // Son 5 işlem
  const recent = [...forms]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  // Kısa yollar (whiteboard — Kısa Yollar)
  const shortcuts = [
    {
      label: t("sahaAnaSayfa.kontrolFormu"),
      desc: t("sahaAnaSayfa.yeniDenetimBaslat"),
      icon: <ClipboardCheck size={22} />,
      color: "primary",
      to: "/FdsSaha/Formlar/denetim",
    },
    {
      label: t("sahaAnaSayfa.uygunsuzluklar"),
      desc: t("sahaAnaSayfa.acikUygunsuzluklariGoruntule"),
      icon: <AlertTriangle size={22} />,
      color: "warning",
      to: "/FdsSaha/Formlar/uygunsuzluk",
    },
    {
      label: t("sahaAnaSayfa.dofTakibi"),
      desc: t("sahaAnaSayfa.duzenleyiciFaaliyetler"),
      icon: <CheckCircle size={22} />,
      color: "success",
      to: "/FdsSaha/Formlar/dof",
    },
    {
      label: t("sahaAnaSayfa.istatistikler"),
      desc: t("sahaAnaSayfa.fuarDenetimOzeti"),
      icon: <BarChart2 size={22} />,
      color: "info",
      to: "/FdsSaha/Istatistikler",
    },
    {
      label: t("sahaAnaSayfa.esnekFormlar"),
      desc: t("sahaAnaSayfa.esnekFormSablonlariniDoldur"),
      icon: <LayoutList size={22} />,
      color: "secondary",
      to: "/FdsSaha/FreeForm",
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("sahaAnaSayfa.anaSayfa")} pageTitle={t("sahaAnaSayfa.fdsSaha")} />

        {/* ── Özet Kartlar ────────────────────────────────────────────────── */}
        <Row className="g-3 mb-4">
          {[
            { label: t("sahaAnaSayfa.toplamDenetim"),     value: totalDenetim,    color: "primary", icon: <ClipboardCheck size={20} /> },
            { label: t("sahaAnaSayfa.devamEden"),          value: inProgress,      color: "secondary", icon: <FileText       size={20} /> },
            { label: t("sahaAnaSayfa.acikUygunsuzluk"),   value: openUygunsuzluk, color: "warning",  icon: <AlertTriangle  size={20} /> },
            { label: t("sahaAnaSayfa.kapatilanDof"),       value: closedDof,       color: "success",  icon: <CheckCircle    size={20} /> },
          ].map((stat) => (
            <Col key={stat.label} xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody className="d-flex align-items-center gap-3">
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center text-${stat.color}`}
                    style={{ width: 48, height: 48, background: `var(--vz-${stat.color}-bg-subtle, #f8f9fa)` }}
                  >
                    {stat.icon}
                  </div>
                  <div>
                    <div className="fw-bold fs-4 lh-1">{stat.value}</div>
                    <div className="text-muted small mt-1">{stat.label}</div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-4">
          {/* ── Kroki (Placeholder) ──────────────────────────────────────── */}
          <Col lg={7}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <MapPin size={18} className="text-danger" />
                  <h6 className="mb-0 fw-semibold">{t("sahaAnaSayfa.kroki")}</h6>
                  <Badge color="secondary" className="ms-auto">{t("sahaAnaSayfa.yakinda")}</Badge>
                </div>
                <div
                  className="d-flex flex-column align-items-center justify-content-center rounded-3 border border-dashed text-muted"
                  style={{ minHeight: 280, background: "#f8f9fa" }}
                >
                  <MapPin size={40} strokeWidth={1} className="mb-3 text-muted opacity-50" />
                  <p className="mb-1 fw-semibold">{t("sahaAnaSayfa.fuarAlaniKrokisi")}</p>
                  <p className="small text-muted mb-0">
                    {t("sahaAnaSayfa.standKonumlariniVeDenetimDurumlarini")}
                  </p>
                  <p className="small text-muted">{t("sahaAnaSayfa.buAlanKullanilacaktir")}</p>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* ── Kısa Yollar ──────────────────────────────────────────────── */}
          <Col lg={5}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="fw-semibold mb-3">{t("sahaAnaSayfa.kisaYollar")}</h6>
                <div className="d-flex flex-column gap-2">
                  {shortcuts.map((s) => (
                    <button
                      key={s.to}
                      className="btn btn-light text-start d-flex align-items-center gap-3 p-3 border rounded-3"
                      style={{ transition: "all .15s" }}
                      onClick={() => navigate(s.to)}
                    >
                      <span className={`text-${s.color}`}>{s.icon}</span>
                      <div>
                        <div className="fw-semibold small">{s.label}</div>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>{s.desc}</div>
                      </div>
                      <i className="bx bx-chevron-right ms-auto text-muted" />
                    </button>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Son İşlemler ──────────────────────────────────────────────────── */}
        <Row className="mt-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-semibold mb-3">{t("sahaAnaSayfa.sonIslemler")}</h6>
                {recent.length === 0 ? (
                  <p className="text-muted text-center py-4">{t("sahaAnaSayfa.henuzKayitYok")}</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("sahaAnaSayfa.tur")}</th>
                          <th>{t("sahaAnaSayfa.kapsam")}</th>
                          <th>{t("sahaAnaSayfa.hedef")}</th>
                          <th>{t("sahaAnaSayfa.tarih")}</th>
                          <th>{t("sahaAnaSayfa.durum")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((f) => {
                          const cfg = LEVEL_CFG[f.inspectionLevelId] || LEVEL_CFG[1];
                          return (
                            <tr key={f.id}>
                              <td>
                                <Badge color={cfg.color} pill className="d-flex align-items-center gap-1" style={{ width: "fit-content" }}>
                                  {cfg.icon} {TYPE_NAMES[f.inspectionTypeId] || t("sahaAnaSayfa.tip", { inspectionTypeId: f.inspectionTypeId })}
                                </Badge>
                              </td>
                              <td className="small text-muted">{SCOPE_NAMES[f.inspectionScopeId] || "—"}</td>
                              <td className="small fw-semibold">{TARGET_NAMES[f.scopeTargetId] || f.scopeTargetId}</td>
                              <td className="small text-muted">{formatDate(f.createdAt)}</td>
                              <td>
                                {f.isDone
                                  ? <Badge color="success"   pill>{t("sahaAnaSayfa.tamamlandi")}</Badge>
                                  : <Badge color="secondary" pill>{t("sahaAnaSayfa.devamEdiyor")}</Badge>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default SahaAnaSayfa;


